import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import API_BASE_URL from '../Config';

const useCajaStore = create(
    persist(
        (set, get) => ({
            // socio: Socio | null
            socio: null,
            // deudas: Deuda[]
            deudas: [],
            // carrito: Deuda[]
            carrito: [],
            // busqueda: string
            busqueda: '',
            // searchResults: Socio[]
            searchResults: [],
            isSearching: false,
            
            // Actions
            setBusqueda: (val) => set({ busqueda: val }),
            
            buscarSocios: async (token, manualQuery = null) => {
                const query = manualQuery !== null ? manualQuery : get().busqueda;
                
                if (query.trim().length < 3) {
                    set({ searchResults: [], isSearching: false });
                    return;
                }
                
                set({ isSearching: true });
                try {
                    const resp = await fetch(`${API_BASE_URL}/socios/buscar?q=${encodeURIComponent(query)}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (resp.ok) {
                        const data = await resp.json();
                        set({ searchResults: data || [] });
                    }
                } catch (e) {
                    console.error("Error buscando socios:", e);
                } finally {
                    set({ isSearching: false });
                }
            },
            
            seleccionarSocio: async (socio, token) => {
                set({ 
                    socio, 
                    searchResults: [], 
                    busqueda: socio.personaFisica ? socio.personaFisica.numeroDocumento : (socio.personaJuridica?.ruc || ''),
                    deudas: [],
                    carrito: []
                });
                
                // Fetch deudas immediately
                try {
                    const resp = await fetch(`${API_BASE_URL}/obligaciones/pendientes?socioId=${socio.id}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    if (resp.ok) {
                        const data = await resp.json();
                        const safeDeudas = (data.obligaciones || []).map(d => ({ 
                            ...d, 
                            uniqueId: `${d.tipo}-${d.id}` 
                        }));
                        set({ deudas: safeDeudas });
                    }
                } catch (e) {
                    console.error("Error cargando deudas del socio:", e);
                }
            },
            
            limpiarSocio: () => set({ 
                socio: null, 
                deudas: [], 
                carrito: [], 
                busqueda: '', 
                searchResults: [] 
            }),
            
            addToCart: (deuda) => {
                const { carrito } = get();
                if (!carrito.find(i => i.uniqueId === deuda.uniqueId)) {
                    set({ carrito: [...carrito, { ...deuda, montoAPagar: Math.round(deuda.monto) }] });
                }
            },
            
            removeFromCart: (uniqueId) => {
                const { carrito } = get();
                set({ carrito: carrito.filter(i => i.uniqueId !== uniqueId) });
            },
            
            toggleSelection: (deuda) => {
                const { carrito, deudas } = get();
                const exists = carrito.find(i => i.uniqueId === deuda.uniqueId);

                const parse = (c) => {
                    const cuotaMatch = c.match(/CUOTA\s+(\d+)/i);
                    const prestamoMatch = c.match(/PRÉSTAMO\s+(\d+)/i);
                    return { cuA: cuotaMatch ? parseInt(cuotaMatch[1]) : 0, prA: prestamoMatch ? prestamoMatch[1] : '' };
                };

                if (exists) {
                    // AL QUITAR: Si es cuota, quitar también todas las posteriores del mismo préstamo
                    if (deuda.tipo === 'CUOTA') {
                        const { cuA, prA } = parse(deuda.concepto);
                        const itemsToRemoveIds = carrito.filter(item => {
                            if (item.tipo !== 'CUOTA') return false;
                            const { cuA: cuB, prA: prB } = parse(item.concepto);
                            return prA === prB && cuB >= cuA;
                        }).map(i => i.uniqueId);

                        set({ carrito: carrito.filter(i => !itemsToRemoveIds.includes(i.uniqueId)) });
                    } else {
                        set({ carrito: carrito.filter(i => i.uniqueId !== deuda.uniqueId) });
                    }
                } else {
                    // AL AGREGAR: Si es cuota, agregar también todas las anteriores del mismo préstamo pendientes
                    if (deuda.tipo === 'CUOTA') {
                        const { cuA, prA } = parse(deuda.concepto);
                        const itemsToAdd = deudas.filter(d => {
                            if (d.tipo !== 'CUOTA') return false;
                            const { cuA: cuB, prA: prB } = parse(d.concepto);
                            return prA === prB && cuB <= cuA;
                        });

                        // Filtrar los que no están ya en el carrito
                        const newItems = itemsToAdd.filter(d => !carrito.find(c => c.uniqueId === d.uniqueId));
                        set({ 
                            carrito: [
                                ...carrito, 
                                ...newItems.map(d => ({ ...d, montoAPagar: Math.round(d.monto) }))
                            ] 
                        });
                    } else {
                        set({ carrito: [...carrito, { ...deuda, montoAPagar: Math.round(deuda.monto) }] });
                    }
                }
            },
            
            limpiarCarrito: () => set({ carrito: [] }),
            
            updateMontoItem: (uniqueId, monto) => {
                const { carrito } = get();
                set({ 
                    carrito: carrito.map(i => i.uniqueId === uniqueId ? { ...i, montoAPagar: monto } : i) 
                });
            }
        }),
        {
            name: 'caja-terminal-storage',
            partialize: (state) => ({ 
                socio: state.socio, 
                deudas: state.deudas, 
                carrito: state.carrito, 
                busqueda: state.busqueda 
            }),
        }
    )
);

export default useCajaStore;
